import type { GeneratedPermissions } from 'mission-control-common-components';
import { useMemo } from 'react';

import type { ConfigMap, DisplayEnum, PacingType } from '../../../../configs';
import { makeConfig } from '../../../../configs';
import type { PermissionsNames } from '../../../../constants';

const sponsorship = 'sponsorship';
const nonGuaranteed = 'non_guaranteed';
const tiersPermittedToUpdate = [sponsorship, nonGuaranteed];

interface UsePacingTypeCellMutationProps {
  pacingType: DisplayEnum<PacingType>;
  tierName: string;
  permissions: Pick<GeneratedPermissions<PermissionsNames>, PermissionsNames.updateLineItemPacingType> | null;
}

interface UsePacingTypeCellMutation {
  isPacingTypeModifiable: boolean;
  permittedPacingTypeOptions: ConfigMap<PacingType>;
}

export const usePacingTypeCellMutation = ({
  pacingType,
  tierName,
  permissions,
}: UsePacingTypeCellMutationProps): UsePacingTypeCellMutation => {
  const isPacingTypeModifiable = useMemo(() => {
    if (!tiersPermittedToUpdate.includes(tierName) || !permissions?.updateLineItemPacingType) {
      return false;
    }

    switch (tierName) {
      case sponsorship:
        return ['AFAP', 'EVEN'].includes(pacingType.key);
      case nonGuaranteed:
        return ['EVEN', 'YIELD'].includes(pacingType.key);
      default:
        return false;
    }
  }, [pacingType, tierName, permissions]);

  const permittedPacingTypeOptions = useMemo(() => {
    if (!isPacingTypeModifiable) {
      return makeConfig<PacingType>({});
    }

    switch (tierName) {
      case sponsorship:
        return makeConfig<PacingType>({ AFAP: 'AFAP', EVEN: 'Even' });
      case nonGuaranteed:
        return makeConfig<PacingType>({ EVEN: 'Even', YIELD: 'Yield' });
      default:
        return makeConfig<PacingType>({});
    }
  }, [tierName, isPacingTypeModifiable]);

  return {
    isPacingTypeModifiable,
    permittedPacingTypeOptions,
  };
};
